import { useState, useEffect } from 'react';
import { getWindow } from 'ssr-window';
import { useLocalization } from './useLocalization';

const withPrependedItem = (categories, firstItem) => [firstItem, ...Object.values(categories)];

const getIndexOfSelectedOption = (optionValues, categoryOptions, selectedOption) =>
  optionValues.indexOf(categoryOptions[selectedOption]);

const getSelectedOptionKey = (options, selectedOption) => {
  const byIndex = options?.[selectedOption];
  const selectedKey = Object.keys(options).find(
    (key) => options[key] === selectedOption || options[key] === byIndex
  );

  return selectedKey;
};

/**
 * @typedef {{
 *   defaultSelectedCategory: string;
 *   categories: Record<string, string>;
 *   label: string;
 *   state: unknown
 *   setState: import('react').Dispatch<import('react').SetStateAction<unknown>>;
 *   queryStringKey: string;
 *   order?: number
 * }} GenericFilter
 */

/**
 * @param {{
 *  engagementType: string;
 *  industry: string;
 *  service: string;
 *  engagementTypeCategories: Record<string, string>;
 *  serviceCategories: Record<string, string>;
 *  industryCategories: Record<string, string>;
 *  additionalCategories: GenericFilter[];
 * }}
 */
const useInsightGridFilters = ({
  engagementType = '',
  industry = '',
  service = '',
  engagementTypeCategories,
  serviceCategories,
  industryCategories,
  additionalCategories = []
}) => {
  const { resourceStrings } = useLocalization();
  const typeLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Type'];
  const serviceLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Service'];
  const industryLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Industry'];

  const [selectedEngagementType, setSelectedEngagementType] = useState(engagementType);
  const [selectedIndustry, setSelectedIndustry] = useState(industry);
  const [selectedService, setSelectedService] = useState(service);

  const filters = [];

  // It might be better to make everything run through this generic filter creation,
  // instead of having explicit creation for engagement, industry, and service.
  // The downside is you would need to create all of the state management in each component.
  // The upside is ordering of filters is easier and the code here becomes cleaner.
  if (additionalCategories.length) {
    additionalCategories.forEach(
      ({ categories, defaultSelectedCategory, label, state, setState }) => {
        const categoryValues = withPrependedItem(categories, label);
        const indexOfPreselectedCategory = getIndexOfSelectedOption(
          categoryValues,
          categories,
          defaultSelectedCategory
        );

        filters.push({
          filterOptions: categoryValues,
          onChange: setState,
          preselectedOption:
            (state !== null || state !== undefined) && indexOfPreselectedCategory !== -1
              ? indexOfPreselectedCategory
              : 0
        });
      }
    );
  }

  if (engagementTypeCategories) {
    const engagementTypeCategoryValues = withPrependedItem(engagementTypeCategories, typeLabel);
    const indexOfPreselectedEngagementType = getIndexOfSelectedOption(
      engagementTypeCategoryValues,
      engagementTypeCategories,
      selectedEngagementType
    );

    filters.push({
      filterOptions: engagementTypeCategoryValues,
      onChange: setSelectedEngagementType,
      preselectedOption:
        engagementType && indexOfPreselectedEngagementType !== -1
          ? indexOfPreselectedEngagementType
          : 0
    });
  }

  if (serviceCategories) {
    const serviceCategoryValues = withPrependedItem(serviceCategories, serviceLabel);
    const indexOfPreselectedService = getIndexOfSelectedOption(
      serviceCategoryValues,
      serviceCategories,
      selectedService
    );

    filters.push({
      filterOptions: serviceCategoryValues,
      onChange: setSelectedService,
      preselectedOption: service && indexOfPreselectedService !== -1 ? indexOfPreselectedService : 0
    });
  }

  if (industryCategories) {
    const industryCategoryValues = withPrependedItem(industryCategories, industryLabel);
    const indexOfPreselectedIndustry = getIndexOfSelectedOption(
      industryCategoryValues,
      industryCategories,
      selectedIndustry
    );

    filters.push({
      filterOptions: industryCategoryValues,
      onChange: setSelectedIndustry,
      preselectedOption:
        industry && indexOfPreselectedIndustry !== -1 ? indexOfPreselectedIndustry : 0
    });
  }

  return {
    filters,
    selectedEngagementType,
    setSelectedEngagementType,
    selectedIndustry,
    setSelectedIndustry,
    selectedService,
    setSelectedService
  };
};

/**
 * @param {{
 *   initialResults: [];
 *   initialTotalCount: number;
 *   url: string;
 *   apiUrl: string;
 *   apiUrlWithoutSkip: string;
 * }}
 */
const useInsightData = ({ initialResults, initialTotalCount, url, apiUrl, apiUrlWithoutSkip }) => {
  const window = getWindow();
  const [results, setResults] = useState(initialResults);
  const [totalCount, setTotalCount] = useState(initialTotalCount);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    window.history.pushState(null, null, url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const showMore = () => {
    setIsLoading(true);
    fetch(apiUrl)
      .then((response) => response.json())
      .then((response) => {
        if (!response) return;

        setResults([...results, ...response.data]);
        setTotalCount(response.total);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    fetch(apiUrlWithoutSkip)
      .then((response) => response.json())
      .then((response) => {
        if (!response) return;
        setResults(response.data);
        setTotalCount(response.total);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrlWithoutSkip]);

  const hasMoreResults = results && results.length < totalCount;

  return {
    isLoading,
    results,
    hasMoreResults,
    showMore
  };
};

/**
 * @param {{
 *  engagementTypeCategories: Record<string, string>;
 *  selectedEngagementType: string;
 *  serviceCategories: Record<string, string>;
 *  selectedService: string;
 *  industryCategories: Record<string, string>;
 *  selectedIndustry: string;
 *  additionalCategories: GenericFilter[];
 *  trendingTopics: [];
 *  shouldIncludeOnlyCpe: boolean;
 *  skip: number;
 *  nodeId: string;
 *  apiSlug: string;
 *  notContentType: number;
 *  isLanding: boolean;
 * }}
 */
const useInsightFilterUrls = ({
  engagementTypeCategories,
  selectedEngagementType,
  serviceCategories,
  selectedService,
  industryCategories,
  selectedIndustry,
  additionalCategories,
  trendingTopics,
  shouldIncludeOnlyCpe,
  skip,
  nodeId,
  apiSlug,
  notContentType,
  isLanding
}) => {
  const window = getWindow();
  const { resourceStrings } = useLocalization();
  const typeLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Type'];
  const serviceLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Service'];
  const industryLabel = resourceStrings['bdO.InsightLanding.FilterLabel.Industry'];
  const [take, setTake] = useState(window.innerWidth > 768 ? 20 : 8);

  useEffect(() => {
    setTake(window.innerWidth > 768 ? 20 : 8);
  }, [window.innerWidth]);

  const pathName = window.location.pathname;
  const params = new URLSearchParams();
  params.append('isLanding', isLanding);
  if (notContentType === 2) {
    params.append('notContentType', 2);
  }
  if (engagementTypeCategories && selectedEngagementType && selectedEngagementType !== typeLabel) {
    const categoryKey = getSelectedOptionKey(engagementTypeCategories, selectedEngagementType);
    params.append('category', categoryKey);
  }

  if (serviceCategories && selectedService && selectedService !== serviceLabel) {
    const serviceKey = getSelectedOptionKey(serviceCategories, selectedService);
    params.append('service', serviceKey);
  }

  if (industryCategories && selectedIndustry && selectedIndustry !== industryLabel) {
    const industryKey = getSelectedOptionKey(industryCategories, selectedIndustry);
    params.append('industry', industryKey);
  }

  if (trendingTopics?.length) {
    params.append('trendingTopics', trendingTopics.join(''));
  }

  if (additionalCategories.length) {
    additionalCategories.forEach(({ categories, state, label, queryStringKey }) => {
      const key = getSelectedOptionKey(categories, state);
      if ((state !== null || state !== undefined) && state !== label) {
        params.append(queryStringKey, key);
      }
    });
  }

  if (shouldIncludeOnlyCpe) {
    params.append('shouldIncludeOnlyCpe', shouldIncludeOnlyCpe);
  }

  const url = `${pathName}?${params.toString()}`;

  if (nodeId) {
    params.append('nodeId', nodeId);
  }

  if (take) {
    params.append('take', take);
  }

  const apiUrlWithoutSkip = `/api/${apiSlug}?${params.toString()}`;

  if (skip) {
    params.append('skip', skip);
  }

  const apiUrl = `/api/${apiSlug}?${params.toString()}`;

  const trimTrailingCharacters = (str) => str.trimEnd('&').trimEnd('?');

  return {
    url: trimTrailingCharacters(url),
    apiUrl: trimTrailingCharacters(apiUrl),
    apiUrlWithoutSkip: trimTrailingCharacters(apiUrlWithoutSkip)
  };
};

/**
 * @param {{
 *  apiSlug: string;
 *  initialResults: [];
 *  initialTotalCount: number;
 *  engagementTypeCategories: Record<string, string>;
 *  industryCategories: Record<string, string>;
 *  serviceCategories: Record<string, string>;
 *  shouldIncludeOnlyCpe: boolean;
 *  nodeId: string;
 *  trendingTopics: [];
 *  additionalCategories: GenericFilter;
 * }}
 */
const useInsights = ({
  apiSlug,
  initialResults,
  initialTotalCount,
  engagementTypeCategories,
  industryCategories,
  serviceCategories,
  selectedEngagementType,
  selectedIndustry,
  selectedService,
  additionalCategories,
  shouldIncludeOnlyCpe,
  nodeId,
  trendingTopics,
  notContentType,
  isLanding
}) => {
  const [skip, setSkip] = useState(initialResults?.length);

  const gridFilters = useInsightGridFilters({
    engagementTypeCategories,
    industryCategories,
    serviceCategories,
    engagementType: selectedEngagementType,
    industry: selectedIndustry,
    service: selectedService,
    additionalCategories
  });

  const urls = useInsightFilterUrls({
    engagementTypeCategories,
    selectedEngagementType: gridFilters.selectedEngagementType,
    serviceCategories,
    selectedService: gridFilters.selectedService,
    industryCategories,
    selectedIndustry: gridFilters.selectedIndustry,
    additionalCategories,
    apiSlug,
    skip,
    shouldIncludeOnlyCpe,
    nodeId,
    trendingTopics,
    notContentType,
    isLanding
  });

  const data = useInsightData({
    url: urls.url,
    apiUrl: urls.apiUrl,
    apiUrlWithoutSkip: urls.apiUrlWithoutSkip,
    initialResults,
    initialTotalCount,
    selectedEngagementType: gridFilters.selectedEngagementType,
    selectedService: gridFilters.selectedService,
    selectedIndustry: gridFilters.selectedIndustry,
    additionalCategories
  });

  useEffect(() => {
    if (data.isLoading || !data.results) return;

    setSkip(data.results.length);
  }, [data.isLoading, data.results, setSkip]);

  return {
    ...gridFilters,
    ...urls,
    ...data
  };
};

export default useInsights;
